import { Box, Stack, TextField, Typography } from "@mui/material";
import {
  useState,
  useRef,
  forwardRef,
  useImperativeHandle,
  useEffect,
} from "react";

const UnitNumberForm = forwardRef(({ onDataChange, initialData }, ref) => {
  const [unitPrefix, setUnitPrefix] = useState("");
  const [unitPrefixError, setUnitPrefixError] = useState(false);
  const [unitPrefixErrorMsg, setUnitPrefixErrorMsg] = useState("");
  const unitPrefixRef = useRef(null);
  const maxLength = 50;

  const [unitNumber, setUnitNumber] = useState(1);
  const [unitNumberError, setUnitNumberError] = useState(false);
  const [unitNumberErrorMsg, setUnitNumberErrorMsg] = useState("");
  const unitNumberRef = useRef(null);
  const maxStartNumberLength = 10;

  // Define form state for text inputs and select field
  const [formData, setFormData] = useState({
    unitPrefix: initialData?.unitPrefix || "",
    unitNumber: initialData?.unitNumber || "",
  });

  useEffect(() => {
    // Run once on mount: initialize the state from initialData
    if (initialData) {
      setFormData(initialData);
    }
  }, []);

  // Handle text/select input changes & update formData state
  const handleChange = (e) => {
    const { name, value } = e.target;

    if (name === "unitPrefix") {
      setUnitPrefix(value);
      setUnitPrefixError(false);
      setUnitPrefixErrorMsg("");

      if (value.length >= maxLength) {
        setUnitPrefixErrorMsg(
          `This field cannot be more than ${maxLength} characters`
        );
      }
    }

    if (name === "unitNumber") {
      const numericValue = parseInt(value, 10) || 1;
      setUnitNumber(numericValue);
      setUnitNumberError(false);
      setUnitNumberErrorMsg("");

      if (numericValue.toString().length >= maxStartNumberLength) {
        setUnitNumberErrorMsg(
          `This field cannot be more than ${maxStartNumberLength} characters`
        );
      }
    }

    setFormData((prevData) => ({
      ...prevData,
      [name]: value,
    }));

    // Notify parent of data changes
    onDataChange({
      ...formData,
      [name]: value,
    });
  };

  // Validate all form fields on submit
  const validateUserInput = () => {
    // Validate unitPrefix Length
    if (formData.unitPrefix.length > maxLength) {
      setUnitPrefixError(true);
      setUnitPrefixErrorMsg(
        `This field cannot be more than ${maxLength} characters`
      );
      unitPrefixRef.current.focus();
      return false;
    } else {
      setUnitPrefixError(false);
      setUnitPrefixErrorMsg("");
    }

    // Validate unitNumber
    if (isNaN(parseInt(formData.unitNumber, 10))) {
      // console.log("HERE");
      setUnitNumberError(true);
      setUnitNumberErrorMsg("This field is required");
      unitNumberRef.current.focus();
      return false;
    } else if (formData.unitNumber.length > maxStartNumberLength) {
      setUnitNumberError(true);
      setUnitNumberErrorMsg(
        `This field cannot be more than ${maxStartNumberLength} characters`
      );
      unitNumberRef.current.focus();
      return false; // Prevent form submission
    }

    return true;
  };

  // Expose the validate method to the parent via ref
  useImperativeHandle(ref, () => ({
    validateUserInput,
    // To fetch all form data from UnitForm:
    getFormData: () => ({
      //   ...formData,
      unitPrefix: formData.unitPrefix,
      unitNumber: parseInt(formData.unitNumber, 10),
    }),
  }));

  return (
    <>
      <Box sx={{ width: { xs: "75%", sm: 400 } }}>
        <Stack direction="row" spacing={2} sx={{ width: "100%" }}>
          <TextField
            type="text"
            label="Unit Prefix"
            name="unitPrefix"
            value={formData.unitPrefix}
            inputRef={unitPrefixRef}
            onChange={handleChange}
            autoComplete="off"
            fullWidth
            slotProps={{
              htmlInput: {
                maxLength: maxLength, // Set maxLength to 50 characters
              },
            }}
            error={unitPrefixError} // Show error if limit is reached
            helperText={
              <Typography
                variant="body4"
                sx={{ color: `${unitPrefixError && "error"}` }}
              >
                {unitPrefixErrorMsg}
              </Typography>
            }
          />
          <TextField
            label="Unit Number"
            type="number"
            name="unitNumber"
            inputRef={unitNumberRef}
            value={formData.unitNumber}
            onChange={handleChange}
            error={unitNumberError}
            helperText={
              <Typography
                variant="body4"
                sx={{ color: `${unitNumberError && "error"}` }}
              >
                {unitNumberErrorMsg}
              </Typography>
            }
            autoComplete="off"
            fullWidth
            required
            slotProps={{
              htmlInput: {
                min: 1,
                onKeyDown: (e) => {
                  // Prevent "e", "E", "+", and "-" from being entered
                  if (["e", "E", "+", "-"].includes(e.key)) {
                    e.preventDefault();
                  }
                },
              },
            }}
          />
        </Stack>
      </Box>
    </>
  );
});

export default UnitNumberForm;
